import { useState, useEffect } from 'react'
import { X, BookOpen } from 'lucide-react'
import { api, KbCategory } from '../../lib/api'

interface Props {
  content: string
  sessionId?: string | null
  onClose: () => void
  onSaved: () => void
}

export function KbSaveFromSolutionModal({ content, sessionId, onClose, onSaved }: Props) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [tagsInput, setTagsInput] = useState('')
  const [categories, setCategories] = useState<KbCategory[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api.solutions.kb.categories.list().then(setCategories).catch(() => {})
  }, [])

  async function handleSave() {
    if (!title.trim()) { setError('Please enter a title'); return }
    setError(null)
    setSaving(true)
    try {
      await api.solutions.kb.createFromSolution({
        title: title.trim(),
        contentMd: content,
        sessionId: sessionId || undefined,
        categoryId: categoryId || undefined,
        tags: tagsInput ? tagsInput.split(',').map(t => t.trim()).filter(Boolean) : undefined,
        description: description || undefined,
      })
      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-indigo-600" />
            Save to Knowledge Base
          </h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Title *</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Enter a title for this entry"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
              autoFocus
              onKeyDown={e => e.key === 'Enter' && handleSave()}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
              <select
                value={categoryId}
                onChange={e => setCategoryId(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:border-indigo-500 outline-none"
              >
                <option value="">None</option>
                {categories.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Tags</label>
              <input
                type="text"
                value={tagsInput}
                onChange={e => setTagsInput(e.target.value)}
                placeholder="tag1, tag2, ..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-indigo-500 outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Brief description (optional)"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-indigo-500 outline-none"
            />
          </div>

          {/* Content preview */}
          <div className="bg-gray-50 rounded-lg p-3 max-h-24 overflow-y-auto">
            <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1">Content Preview</p>
            <p className="text-xs text-gray-600 line-clamp-4 whitespace-pre-wrap">
              {content.slice(0, 300)}{content.length > 300 ? '...' : ''}
            </p>
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !title.trim()}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
          >
            <BookOpen className="w-3.5 h-3.5" />
            {saving ? 'Saving...' : 'Save to KB'}
          </button>
        </div>
      </div>
    </div>
  )
}
